import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import pdfDataCache from '../documents/pdfDataCache';

// Types
export const DomainValueType = {
  domain_value_id: '',
  domain_name: '',
  technical_name: '',
  description: '',
  sikb_id: '',
  definition: '',
  group_name: '',
  cas_nr: '',
  aquo_code: '',
  additional_info: '',
  unit_dimension: '',
  unit_factor: '',
  timestamp_active: null,
  timestamp_inactive: null,
  bro_sad_imbro: '',
  bro_sad_imbro_a: ''
};

// Hook voor het ophalen van domain values
// Accepts boolean (activeOnly), array (technicalNames), or object (filters)
export const useDomainValues = (options = true) => {
  const [domainValues, setDomainValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const optionsKey = useMemo(() => {
    if (Array.isArray(options)) {
      const sortedOptions = [...options].sort(); 
      return JSON.stringify(sortedOptions);
    } else if (typeof options === 'object' && options !== null) {
      const sortedOptions = Object.keys(options).sort().reduce((acc, key) => {
        if (typeof options[key] !== 'function') {
          acc[key] = options[key];
        }
        return acc;
      }, {});
      return JSON.stringify(sortedOptions);
    }
    return String(options); 
  }, [options]);

  useEffect(() => {
    const fetchDomainValues = async () => {
      try {
        setLoading(true);
        setError(null);

        const documentId = 'global'; 
        const tabName = 'domain_values_lookup';
        const extraKey = optionsKey;
        
        const cachedData = pdfDataCache.get(documentId, tabName, extraKey);
        
        if (cachedData) {
          setDomainValues(cachedData);
          setLoading(false);
          return;
        }

        let data, queryError;
        let activeOnly = true; // Default
        let technical_name, group_name, unit_dimension, sortByFrequency = false; // Declare variables

        if (typeof options === 'object' && options !== null && !Array.isArray(options)) {
          ({ 
            technical_name, 
            group_name, 
            unit_dimension, 
            activeOnly: activeOnly = true, // Default to true if not provided
            sortByFrequency = false // Default to false if not provided
          } = options);
        } else if (Array.isArray(options)) {
          technical_name = options; // Store array here for later use
        } else if (typeof options === 'boolean') {
          activeOnly = options;
        }

        const isHoedanigheidReferentieVlak = 
          technical_name === 'Hoedanigheid' && 
          group_name === 'ReferentieVlak';

        if (sortByFrequency && isHoedanigheidReferentieVlak) {
          const { data: rpcData, error: rpcError } = await supabase.rpc(
            'get_domain_values_with_frequency',
            {
              p_technical_name: technical_name,
              p_group_name: group_name
            }
          );
          data = rpcData;
          queryError = rpcError;

          if (data) {
            data = data.map(item => ({ ...item, technical_name: technical_name }));
          }
        } else {
          let query = supabase
            .from('document_domainvalueslookup')
            .select('*');

          if (typeof options === 'object' && options !== null && !Array.isArray(options)) {
            if (technical_name) {
              if (Array.isArray(technical_name)) {
                 query = query.in('technical_name', technical_name);
              } else {
                 query = query.ilike('technical_name', technical_name);
              }
            } else {
              query = query.not('technical_name', 'is', null);
            }
            if (group_name) {
              query = query.ilike('group_name', group_name);
            }
            if (unit_dimension) {
              query = query.ilike('unit_dimension', unit_dimension);
            }
          } else if (Array.isArray(options)) {
             query = query.in('technical_name', technical_name)
                        .not('technical_name', 'is', null);
          } else {
            query = query.not('technical_name', 'is', null);
          }
          
          if (activeOnly) {
            query = query.is('timestamp_inactive', null);
          }
          const { data: queryData, error: standardError } = await query;
          data = queryData;
          queryError = standardError;
        }
        
        if (queryError) throw queryError;

        const grouped = (data || []).reduce((acc, item) => {
          const key = item.technical_name ? item.technical_name.trim() : 'null_technical_name';
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {});

        pdfDataCache.set(documentId, tabName, grouped, extraKey, 1800000);
        
        setDomainValues(grouped);
      } catch (err) {
        console.error('Error fetching domain values:', err); 
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDomainValues();
  }, [optionsKey]); 

  return { domainValues, loading, error };
};

// Helper functie om een enkele domain value op te halen
export const getDomainValueById = async (domainValueId) => {
  // Check cache first
  const cachedValue = pdfDataCache.get('global', 'domain_value_by_id', domainValueId);
  if (cachedValue) {
    return cachedValue;
  }

  const { data, error } = await supabase
    .from('document_domainvalueslookup')
    .select('*')
    .eq('domain_value_id', domainValueId)
    .single();

  if (error) throw error;
  
  // Cache the result
  pdfDataCache.set('global', 'domain_value_by_id', data, domainValueId);
  
  return data;
};

// Helper functie om domain values te filteren
// This function might become less necessary or could be refactored to use useDomainValues
export const filterDomainValues = async (technicalName) => {
  // Check cache first
  const cachedValues = pdfDataCache.get('global', 'filtered_domain_values', technicalName);
  if (cachedValues) {
    return cachedValues;
  }

  const { data, error } = await supabase
    .from('document_domainvalueslookup')
    .select('*')
    .ilike('technical_name', technicalName)
    .is('timestamp_inactive', null);

  if (error) throw error;
  
  // Cache the result
  pdfDataCache.set('global', 'filtered_domain_values', data, technicalName);
  
  return data;
};

// Helper function to invalidate domain values cache
export const invalidateDomainValuesCache = () => {
  pdfDataCache.clearTab('global', 'domain_values_lookup');
  pdfDataCache.clearTab('global', 'domain_value_by_id');
  pdfDataCache.clearTab('global', 'filtered_domain_values');
};

// Helper functie om te controleren of een domain value actief is
export const isActiveDomainValue = (domainValue) => {
  return !domainValue.timestamp_inactive;
};

// Functie voor server-side zoeken van parameters (stoffen) - GEWIJZIGD VOOR RPC
export const searchParameters = async (searchTerm) => {
  // Voer alleen een zoekopdracht uit als de term lang genoeg is
  if (!searchTerm || searchTerm.trim().length < 2) {
    return []; // Retourneer lege lijst voor korte termen
  }

  const trimmedSearchTerm = searchTerm.trim();

  try {
    // Roep de RPC functie aan in Supabase
    // GEWIJZIGD: Gebruik de correcte parameternaam 'p_search_term'
    const { data, error } = await supabase.rpc('search_parameters_by_similarity', {
      p_search_term: trimmedSearchTerm 
      // We gebruiken de DEFAULT waarden voor p_threshold, p_limit, p_candidate_multiplier in de SQL functie
    });

    if (error) {
      console.error('Error calling search_parameters_by_similarity RPC:', error);
      return []; // Retourneer lege array bij RPC fout
    }

    // De RPC retourneert al de benodigde velden (domain_value_id, description)
    // en is al gesorteerd op relevantie (similarity_score, usage_count)
    return data || []; // Retourneer data of lege array

  } catch (err) {
    console.error('Exception during parameter search RPC call:', err);
    return []; // Retourneer lege array bij een exception
  }
};